import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { Box, CircularProgress, IconButton, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ChevronLeft } from "lucide-react";

import { Badge } from "../../../components/Badge";

import { Button } from "../../../components/Button";
import { ClientReadOnlyScope } from "../../../components/ClientReadOnlyScope";
import { useAuth } from "../../../components/contexts/AuthContext";
import SuccessPopup from "../../../components/SuccessPopup";
import { isClientReadOnly } from "../../../utils/clientAccess";
import { ContentPieceView } from "../ContentPieceView";
import { useContentTutorial } from "../ContentTutorialContext";
import { CONTENT_TUTORIAL_MOCK_PIECE_ID } from "../../../Tutorials/contentGenerator/contentTutorialMocks.js";
import { usePieceDetail } from "./usePieceDetail";

export const INTERNAL_CONTENT_PATH = "/HerramientasIA/contenidos";
export const CLIENT_CONTENT_PATH = "/contenido";

// Back to the list as it was: no client filter is forced from the piece.
export const contentListPath = (isClientView) =>
  isClientView ? CLIENT_CONTENT_PATH : INTERNAL_CONTENT_PATH;

export const piecePath = (isClientView, pieceId) =>
  isClientView ? `${CLIENT_CONTENT_PATH}/${pieceId}` : `${INTERNAL_CONTENT_PATH}/pieza/${pieceId}`;

export const pageFrameSx = {
  display: "flex",
  flexDirection: "column",
  flex: 1,
  minHeight: 0,
  height: { xs: "calc(100dvh - 120px)", md: "calc(100vh - 150px)" },
};

// A saved piece grows with its content and the page scrolls as a whole.
const piecePageFrameSx = {
  display: "flex",
  flexDirection: "column",
  minHeight: { xs: "calc(100dvh - 120px)", md: "calc(100vh - 150px)" },
};

function useToast() {
  const [toast, setToast] = useState({ open: false, message: "", isError: false });
  const notify = useCallback((message, isError = false) => {
    setToast({ open: true, message, isError });
  }, []);
  const element = (
    <SuccessPopup
      message={toast.message}
      isOpen={toast.open}
      onClose={() => setToast((prev) => ({ ...prev, open: false }))}
      error={toast.isError}
      autoHideDuration={3000}
    />
  );
  return { notify, element };
}

export { useToast };

/**
 * Loaded detail of one content piece (the ContentPieceView in edit mode). Shared by the
 * piece page and the piece tabs of a content group.
 */
export function PieceDetail({
  pieceId,
  isClientView = false,
  initialAdaptationId = null,
  initialWorkflowView = null,
  notify,
  onClose,
  closeLabel = "Volver a contenidos",
  onPieceLoaded,
  onStatusChange,
  frameSx = piecePageFrameSx,
}) {
  const tutorial = useContentTutorial();
  const { user } = useAuth();
  // The tutorial's piece only exists as mock data (never fetched, even right after the tutorial ends).
  const isTutorialPiece = String(pieceId) === CONTENT_TUTORIAL_MOCK_PIECE_ID;
  const tutorialPiece = isTutorialPiece ? tutorial.tutorialMock?.piece ?? null : null;
  const {
    piece: loadedPiece,
    setPiece,
    config,
    isLoading,
    loadError,
    saveAdaptationChanges,
    iterateAdaptation,
    iteratingAdaptationId,
    versionsRefreshKey,
  } = usePieceDetail(pieceId, { enabled: !isTutorialPiece, isClientView, notify });
  const piece = isTutorialPiece ? tutorialPiece : loadedPiece;

  useEffect(() => {
    onPieceLoaded?.(piece);
  }, [onPieceLoaded, piece]);

  if (loadError) {
    return (
      <Box sx={{ ...pageFrameSx, alignItems: "center", justifyContent: "center", gap: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {loadError?.response?.status === 404
            ? "Este contenido no existe o ya no tienes acceso."
            : "No se pudo cargar el contenido."}
        </Typography>
        <Button variant="outline" onClick={onClose}>
          {closeLabel}
        </Button>
      </Box>
    );
  }

  if ((isLoading && !isTutorialPiece) || !piece) {
    return (
      <Box sx={{ ...pageFrameSx, alignItems: "center", justifyContent: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ClientReadOnlyScope readOnly={!isClientView && isClientReadOnly(user, config?.client)}>
      <Box sx={frameSx}>
        <ContentPieceView
          key={piece.id}
          mode="edit"
          externalHeader
          onStatusChange={onStatusChange}
          onClose={onClose}
          contentDetail={piece}
          isLoading={false}
          initialAdaptationId={initialAdaptationId}
          initialWorkflowView={initialWorkflowView}
          onSaveChanges={saveAdaptationChanges}
          onIterateAdaptation={iterateAdaptation}
          iteratingAdaptationId={iteratingAdaptationId}
          versionsRefreshKey={versionsRefreshKey}
          onPieceRefreshed={setPiece}
          configId={piece.config}
          initialLanguage={config?.language || "es"}
          isClientView={isClientView}
          hasRawContent={Boolean(piece.raw_content?.trim())}
          hasPlatformAdaptations={(piece.platform_adaptations || []).length > 0}
          pieceTitle={piece.title || ""}
          pieceErrorMessage={piece.error_message || null}
          isTutorialActive={tutorial.isTutorialActive}
          tutorialSelectedPlatforms={isTutorialPiece ? tutorial.tutorialMock?.selectedPlatforms : null}
          tutorialWorkflow={isTutorialPiece ? tutorial.tutorialMock?.workflow ?? null : null}
        />
      </Box>
    </ClientReadOnlyScope>
  );
}

/** Detail page of one content piece: Generación → Aprobación → Correcciones → Programación. */
export function ContentPiecePage({ isClientView = false }) {
  const { pieceId: internalPieceId, contentId } = useParams();
  const pieceId = internalPieceId || contentId;
  const [searchParams] = useSearchParams();
  const initialAdaptationId = Number(searchParams.get("adaptation")) || null;
  const initialWorkflowView = searchParams.get("paso") === "revision" ? "review" : null;
  const navigate = useNavigate();
  const { setPageContext } = useOutletContext();
  const { notify, element: toast } = useToast();

  const theme = useTheme();
  const [piece, setPiece] = useState(null);
  const [headerStatus, setHeaderStatus] = useState(null);
  const listPath = contentListPath(isClientView);
  const goBack = useCallback(() => navigate(listPath), [navigate, listPath]);

  // Same "Tipo: nombre" title as the other detail pages (e.g. Proyecto: …).
  useEffect(() => {
    const pieceTitle = piece?.title || "Contenido";
    setPageContext({
      title: piece ? (
        <>
          Contenido:{"  "}
          <span style={{ color: theme.palette.text.secondary, marginLeft: "4px" }}>{pieceTitle}</span>
        </>
      ) : "",
      showTitle: Boolean(piece),
      leftContent: (
        <IconButton
          onClick={goBack}
          aria-label="Volver a contenidos"
          size="small"
          sx={{ color: "primary.main", padding: "4px", "&:hover": { backgroundColor: "action.hover" } }}
        >
          <ChevronLeft size={20} />
        </IconButton>
      ),
      titleRightContent: headerStatus?.hasErrors ? <Badge color="blocked">Con errores</Badge> : null,
      rightContent: null,
      breadcrumbItems: [
        { label: "Contenidos", onClick: goBack },
        { label: `Contenido: ${pieceTitle}` },
      ],
    });
  }, [setPageContext, goBack, piece, headerStatus, theme.palette.text.secondary]);

  return (
    <>
      {toast}
      <PieceDetail
        pieceId={pieceId}
        isClientView={isClientView}
        initialAdaptationId={initialAdaptationId}
        initialWorkflowView={initialWorkflowView}
        notify={notify}
        onClose={goBack}
        onPieceLoaded={setPiece}
        onStatusChange={setHeaderStatus}
      />
    </>
  );
}
